import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KnowledgeFile } from '../../types';
import { getFileIcon } from '../../utils/file-types';
import { formatFileSize } from '../../utils/file-types';
import { Star, MoreVertical } from 'lucide-react';
import MoreMenu from '../shared/MoreMenu';
// import CategorySelector from '../categories/CategorySelector';
// import { useCategoryStore } from '../../stores/category-store';
import { getSignedUrl } from '../../config/storage';
import { useFileStore } from '../../stores/file-store';
import dayjs from 'dayjs';
import { cn } from '../../utils/cn';
import { useI18n } from '../../i18n';

interface FileListProps {
  files: KnowledgeFile[];
}

const FileList: React.FC<FileListProps> = ({ files }) => {
  const navigate = useNavigate();
  const { starFile, processMissingThumbnails } = useFileStore();
  const [isProcessingThumbnails, setIsProcessingThumbnails] = useState(false);
  const [processingStats, setProcessingStats] = useState<{ processed: number; failed?: number; total?: number } | null>(null);
  const { t } = useI18n();
  const [perFileMenuOpen, setPerFileMenuOpen] = useState<string>('');
  // 预留移动目标/当前文件状态
  // const [moveTarget, setMoveTarget] = useState<string>('');
  // const [movingFileId, setMovingFileId] = useState<string | null>(null);

  const handleStarClick = (e: React.MouseEvent, fileId: string, starred: boolean) => {
    e.stopPropagation();
    starFile(fileId, !starred);
  };

  const handleDownload = async (e: React.MouseEvent, _fileId: string, name: string, storageKey?: string) => {
    e.stopPropagation();
    if (!storageKey) return;
    try {
      const url = await getSignedUrl(storageKey);
      const res = await fetch(url);
      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('Download failed', err);
    }
  };

  const openMoveModal = (_fileId: string) => {
    // 预留移动弹窗逻辑
  };

  // 移动确认弹窗留待后续集成 CategorySelector，这里只记录状态

  const handleGenerateThumbnails = async () => {
    if (isProcessingThumbnails) return;

    try {
      setIsProcessingThumbnails(true);
      setProcessingStats(null);

      const result = await processMissingThumbnails();
      setProcessingStats(result);
      if (result.processed === 0) {
        alert(t('noFilesFound'));
      } else {
        const failedSuffix = result.failed ? ` (${'failed'}: ${result.failed})` : '';
        alert(t('processedStats', { processed: result.processed, total: result.total, failed: failedSuffix } as any));
      }
    } catch (error) {
      console.error('Error generating thumbnails:', error);
      alert(t('processThumbFailed', { message: 'Console for details' } as any));
    } finally {
      setIsProcessingThumbnails(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium">{t('fileListTitle')}</h2>

        <div className="flex space-x-2">
          <button
            onClick={handleGenerateThumbnails}
            disabled={isProcessingThumbnails}
            className="px-3 py-1 text-sm bg-teal-100 text-teal-700 rounded hover:bg-teal-200 flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessingThumbnails ? (
              <>
                <span className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></span>
                <span>{t('processing')}</span>
              </>
            ) : (
              <span>{t('generateThumbnails')}</span>
            )}
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              // Show menu
            }}
            className="p-1 text-gray-400 rounded-full hover:text-gray-600 hover:bg-gray-100"
          >
            <MoreVertical size={16} />
          </button>
        </div>
      </div>

      {processingStats && processingStats.total && processingStats.total > 0 && (
        <div className="p-2 bg-teal-50 text-teal-700 text-sm rounded">
          {t('processedStats', { processed: processingStats.processed, total: processingStats.total, failed: processingStats.failed ? ` (${'failed'}: ${processingStats.failed})` : '' } as any)}
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden md:block overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-5/12">{t('headerName')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/12">{t('headerType')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">{t('headerSize')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/12">{t('headerModified')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/12">{t('headerTags')}</th>
              <th className="relative px-6 py-3">
                <span className="sr-only">{t('headerActions')}</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {files.map((file) => {
              const FileIcon = getFileIcon(file.type);
              return (
                <tr
                  key={file.id}
                  onClick={() => navigate(`/uadmin/files/${file.id}`)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={cn(
                        "flex-shrink-0 h-12 w-12 rounded-lg flex items-center justify-center",
                        file.type === 'document' && 'bg-blue-50',
                        file.type === 'pdf' && 'bg-orange-50',
                        file.type === 'spreadsheet' && 'bg-emerald-50',
                        file.type === 'presentation' && 'bg-purple-50',
                        file.type === 'video' && 'bg-red-50',
                        file.type === 'audio' && 'bg-yellow-50',
                        file.type === 'code' && 'bg-gray-100',
                        file.type === 'other' && 'bg-gray-50'
                      )}>
                        <FileIcon className={cn(
                          "h-6 w-6",
                          file.type === 'document' && 'text-blue-500',
                          file.type === 'pdf' && 'text-orange-500',
                          file.type === 'spreadsheet' && 'text-emerald-500',
                          file.type === 'presentation' && 'text-purple-500',
                          file.type === 'video' && 'text-red-500',
                          file.type === 'audio' && 'text-yellow-500',
                          file.type === 'code' && 'text-gray-700',
                          file.type === 'other' && 'text-gray-500'
                        )} />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-md">{file.name}</div>
                        {file.description && (
                          <div className="text-sm text-gray-500 truncate max-w-md">{file.description}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={cn(
                      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize",
                      file.type === 'document' && 'bg-blue-50 text-blue-800',
                      file.type === 'pdf' && 'bg-orange-50 text-orange-800',
                      file.type === 'spreadsheet' && 'bg-emerald-50 text-emerald-800',
                      file.type === 'presentation' && 'bg-purple-50 text-purple-800',
                      file.type === 'video' && 'bg-red-50 text-red-800',
                      file.type === 'audio' && 'bg-yellow-50 text-yellow-800',
                      file.type === 'code' && 'bg-gray-100 text-gray-800',
                      file.type === 'other' && 'bg-gray-50 text-gray-800'
                    )}>
                      {file.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatFileSize(file.size)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {file.updatedAt ? dayjs(file.updatedAt).format('YYYY-MM-DD HH:mm') : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {(file.tags || []).slice(0, 2).map((tag) => (
                        <span
                          key={tag.id}
                          className="inline-flex items-center px-2 py-0.5 text-xs rounded-full"
                          style={{ backgroundColor: `${tag.color}20`, color: tag.color }}
                        >
                          {tag.name}
                        </span>
                      ))}
                      {file.tags && file.tags.length > 2 && (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">
                          +{file.tags.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={(e) => handleStarClick(e, file.id, file.starred || false)}
                        title={file.starred ? t('removeFromStarred') : t('addToStarred')}
                        className={cn(
                          'flex items-center justify-center p-2 rounded-full transition-colors min-w-[44px] min-h-[44px]',
                          file.starred
                            ? 'text-yellow-600 bg-yellow-50'
                            : 'text-gray-600 hover:text-yellow-600 hover:bg-gray-100'
                        )}
                      >
                        <Star size={18} fill={file.starred ? 'currentColor' : 'none'} />
                      </button>
                      <div className="relative" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => setPerFileMenuOpen(perFileMenuOpen === file.id ? '' : file.id)}
                          className="flex items-center justify-center p-2 text-gray-600 rounded-full hover:text-gray-800 hover:bg-gray-100 min-w-[44px] min-h-[44px]"
                          title={t('more')}
                          aria-label={t('more')}
                        >
                          <MoreVertical size={18} />
                        </button>
                        {perFileMenuOpen === file.id && (
                          <div className="absolute right-0 mt-2">
                            <MoreMenu
                              onRename={undefined}
                              onMove={() => { setPerFileMenuOpen(''); openMoveModal(file.id); }}
                              onDownload={() => { setPerFileMenuOpen(''); const evt = new MouseEvent('click'); handleDownload(evt as any, file.id, file.name, (file as any).storage_key); }}
                              onDelete={() => { setPerFileMenuOpen(''); setIsProcessingThumbnails(false); /* placeholder for future */ }}
                              onClose={() => setPerFileMenuOpen('')}
                              labels={{ rename: t('renameFile'), moveTo: t('moveTo'), download: t('downloadFile'), delete: t('delete') }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile stacked list rows */}
      <div className="md:hidden divide-y divide-gray-200">
        {files.map((file) => (
          <div
            key={file.id}
            className="flex items-center py-3"
            onClick={() => navigate(`/uadmin/files/${file.id}`)}
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">{file.name}</div>
              <div className="text-xs text-gray-500 mt-0.5 truncate">
                <span className="capitalize">{file.type}</span>
                <span className="mx-1">·</span>
                <span>{formatFileSize(file.size)}</span>
                {file.updatedAt && (
                  <>
                    <span className="mx-1">·</span>
                    <span>{dayjs(file.updatedAt).format('YYYY-MM-DD')}</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={(e) => handleStarClick(e, file.id, file.starred || false)}
                className={cn(
                  'flex items-center justify-center p-2 rounded-full transition-colors min-w-[44px] min-h-[44px]',
                  file.starred
                    ? 'text-yellow-600 bg-yellow-50'
                    : 'text-gray-600 hover:text-yellow-600 hover:bg-gray-100'
                )}
                aria-label={t('starred')}
                title={file.starred ? t('removeFromStarred') : t('addToStarred')}
              >
                <Star size={18} fill={file.starred ? 'currentColor' : 'none'} />
              </button>
              <div className="relative">
                <button
                  className="flex items-center justify-center p-2 text-gray-600 rounded-full hover:text-gray-800 hover:bg-gray-100 min-w-[44px] min-h-[44px]"
                  onClick={() => setPerFileMenuOpen(perFileMenuOpen === file.id ? '' : file.id)}
                  aria-label={t('more')}
                  title={t('more')}
                >
                  <MoreVertical size={18} />
                </button>
                {perFileMenuOpen === file.id && (
                  <div className="absolute right-0 mt-2">
                    <MoreMenu
                      onRename={undefined}
                      onMove={() => { setPerFileMenuOpen(''); openMoveModal(file.id); }}
                      onDownload={() => { setPerFileMenuOpen(''); const evt = new MouseEvent('click'); handleDownload(evt as any, file.id, file.name, (file as any).storage_key); }}
                      onDelete={() => { setPerFileMenuOpen(''); setIsProcessingThumbnails(false); /* placeholder */ }}
                      onClose={() => setPerFileMenuOpen('')}
                      labels={{ rename: t('renameFile'), moveTo: t('moveTo'), download: t('downloadFile'), delete: t('delete') }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FileList